const MAX_TRADEMARK_IMAGE_SIZE = 2 * 1024 * 1024
const MAX_TRADEMARK_IMAGE_WIDTH = 2835
const MAX_TRADEMARK_IMAGE_HEIGHT = 2010

// Shared by the EP1001 patent documents form (DocumentsSection) and the
// EP1200 entry-into-European-phase form (PatentEpPhaseFormPage).
export const MAX_PATENT_DOCUMENT_SIZE = 20 * 1024 * 1024

function validatePatentFile(
  file: File,
  extensions: string[],
  typeLabel: string
): string | null {
  if (!hasExtension(file, extensions)) {
    return `Only ${typeLabel} files are allowed.`
  }
  if (file.size > MAX_PATENT_DOCUMENT_SIZE) {
    return "The file must not exceed 20 MB."
  }
  return null
}

export function validatePatentPdfFile(file: File): string | null {
  return validatePatentFile(file, [".pdf"], "PDF")
}

export function validatePatentZipFile(file: File): string | null {
  return validatePatentFile(file, [".zip"], "ZIP")
}

// The EPO takes a sequence listing in computer-readable form only: ST.26 as
// XML or ZIP, ST.25 as APP, TXT or ZIP.
export const SEQUENCE_LISTING_FILE_TYPES = {
  ST25: { extensions: [".app", ".txt", ".zip"], label: "APP, TXT or ZIP" },
  ST26: { extensions: [".xml", ".zip"], label: "XML or ZIP" }
} as const

export function validatePatentSequenceListingFile(
  standard: "ST25" | "ST26"
): (file: File) => string | null {
  const { extensions, label } = SEQUENCE_LISTING_FILE_TYPES[standard]

  return (file) => validatePatentFile(file, [...extensions], label)
}

function hasExtension(file: File, extensions: string[]) {
  const name = file.name.toLowerCase()
  return extensions.some(extension => name.endsWith(extension))
}

function getImageDimensions(file: File) {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const image = new Image()
    const objectUrl = URL.createObjectURL(file)

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve({ width: image.width, height: image.height })
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error("The image could not be read."))
    }

    image.src = objectUrl
  })
}

//Typ und Größencheck für die Datei die hochgeladen werden soll
export function validateFile(file: File): string | null {
  if (!hasExtension(file, [".jpg", ".jpeg"])) {
    return "Only JPG files are allowed."
  }
  if (file.size > MAX_TRADEMARK_IMAGE_SIZE) {
    return "The file must not exceed 2 MB."
  }
  return null
}

export async function validateTrademarkImageFile(file: File): Promise<string | null> {
  const validationError = validateFile(file)
  if (validationError) return validationError

  let width = 0
  let height = 0

  try {
    const dimensions = await getImageDimensions(file)
    width = dimensions.width
    height = dimensions.height
  } catch {
    return "The image could not be read."
  }

  if (width > MAX_TRADEMARK_IMAGE_WIDTH || height > MAX_TRADEMARK_IMAGE_HEIGHT) {
    return `The image must not exceed ${MAX_TRADEMARK_IMAGE_WIDTH} x ${MAX_TRADEMARK_IMAGE_HEIGHT} pixels.`
  }

  return null
}

//Vorschau erstellen
export function generatePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

//Drag & Drop
export function handleFileDrop(
  e: React.DragEvent<HTMLDivElement>,
  onFileSelect: (file: File) => void
) {
  e.preventDefault()
  const file = e.dataTransfer.files?.[0]
  if (file) onFileSelect(file)
}

//File to Base64
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
